import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { IBadResult } from 'src/app/interfaces/bad-result';
import { PasswordModel } from 'src/app/models/password-model';
import { NotificationService } from 'src/app/services/notification.service';
import { ProfileService } from 'src/app/services/profile.service';

@Component({
  selector: 'opt-password',
  templateUrl: './password.component.html',
  styleUrls: ['./password.component.scss'],
})
export class PasswordComponent implements OnInit {
  public passwordForm: UntypedFormGroup;
  public badResult: IBadResult;
  public isPasswordHidden = true;
  public isCurrentPasswordHidden = true;
  public isLoading = false;

  private model: PasswordModel = new PasswordModel();

  constructor(
    private dialogRef: MatDialogRef<PasswordComponent>,
    private profileService: ProfileService,
    private notificationService: NotificationService
  ) {}

  get password() {
    return this.passwordForm.get('password');
  }
  get confirmation() {
    return this.passwordForm.get('confirmation');
  }
  get current() {
    return this.passwordForm.get('current');
  }

  ngOnInit(): void {
    this.passwordForm = new UntypedFormGroup(
      {
        password: new UntypedFormControl(
          '',
          Validators.compose([
            Validators.required,
            this.validatePattern(/\d/, { hasNumber: true }),
            this.validatePattern(/[A-Z]/, { hasCapitalCase: true }),
            this.validatePattern(/[a-z]/, { hasSmallCase: true }),
            Validators.minLength(8),
          ])
        ),
        confirmation: new UntypedFormControl(''),
        current: new UntypedFormControl('', Validators.required),
      },
      { validators: this.passwordsCompareValidator }
    );
  }

  public change(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }
    this.badResult = null;
    this.startLoading();
    this.model.populateFromFormGroup(this.passwordForm);
    this.profileService.changePassword(this.model).subscribe(
      (result) => {
        if (this.badResult) {
          this.badResult = null;
        }
        this.notificationService.show(result.message, { type: 'success' });
        this.dialogRef.close();
      },
      (error) => {
        this.badResult = error.error;
        this.stopLoading();
      },
      () => {
        this.stopLoading();
      }
    );
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  private passwordsCompareValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    if (!this.passwordForm) {
      return null;
    }

    const password = this.password.value;
    const confirmation = this.confirmation.value;

    return password === confirmation ? null : { notEqual: true };
  };

  private startLoading() {
    this.isLoading = true;
    this.passwordForm.disable();
  }

  private stopLoading() {
    this.isLoading = false;
    this.passwordForm.enable();
  }

  private validatePattern(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const valid = regex.test(control.value);
      return valid ? null : error;
    };
  }
}
